import PropTypes from 'prop-types';
import React from 'react';
import LinkifyIt from 'linkify-it';
import tlds from 'tlds';

const linkify = new LinkifyIt();
linkify.tlds(tlds);

linkify.add('@', {
  validate: (text, pos, self) => {
    const tail = text.slice(pos);

    if (!self.re.username) {
      self.re.username = new RegExp(
        '^([a-zA-Z0-9_]){3,20}(?!_)(?=$|' + self.re.src_ZPCc + ')'
      );
    }

    if (self.re.username.test(tail)) {
      // Linkifier allows punctuation chars before prefix,
      // but we additionally disable `@` ("@@mention" is invalid)
      if (pos >= 2 && tail[pos - 2] === '@') {
        return false;
      }
      return tail.match(self.re.username)[0].length;
    }
    return 0;
  },
  normalize: (match) => {
    match.url = '/' + match.url.replace(/^@/, '');
  },
});

linkify.add('#', {
  validate: (text, pos, self) => {
    const tail = text.slice(pos);

    if (!self.re.hashtag) {
      self.re.hashtag = new RegExp(
        '^([a-zA-Z0-9_]){1,100}(?!_)(?=$|' + self.re.src_ZPCc + ')'
      );
    }

    if (self.re.hashtag.test(tail)) {
      // Linkifier allows punctuation chars before prefix,
      // but we additionally disable `#` ("##hashtag" is invalid)
      if (pos >= 2 && tail[pos - 2] === '#') {
        return false;
      }
      return tail.match(self.re.hashtag)[0].length;
    }
    return 0;
  },
  normalize: (match) => {
    match.url = '/browse/' + match.url.replace(/^#/, '%23');
  },
});

class Linkify extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    component: PropTypes.any,
    properties: PropTypes.object,
    urlRegex: PropTypes.object,
    emailRegex: PropTypes.object,
    suggestedRoles: PropTypes.array,
    suggestedSkills: PropTypes.array,
    children: PropTypes.any,
  };

  static defaultProps = {
    className: 'Linkify',
    component: 'a',
    properties: {},
    suggestedRoles: [],
    suggestedSkills: [],
  };

  getMatches(string) {
    const matches = linkify.match(string);

    if (matches) {
      Object.keys(matches).map(
        function (key) {
          if (matches[key].schema === '#') {
            matches[key] = this.parseHashTags(matches[key]);
          }
        }.bind(this)
      );
    }

    return matches;
  }

  parseHashTags(match) {
    if (this.props.suggestedRoles.length > 0) {
      this.props.suggestedRoles.forEach((role) => {
        if (
          typeof role !== 'undefined' &&
          (role.slug.replace('-', '') === match.text.replace('#', '') ||
            role.slug_plural.replace('-', '') === match.text.replace('#', ''))
        ) {
          match.url = '/find/' + role.slug_plural;
        }
      });
    }

    if (this.props.suggestedSkills.length > 0) {
      this.props.suggestedSkills.forEach((skill) => {
        if (
          typeof skill !== 'undefined' &&
          (skill.slug.replace('-', '') === match.text.replace('#', '') ||
            skill.slug_plural.replace('-', '') === match.text.replace('#', ''))
        ) {
          match.url = '/get/' + skill.slug_plural;
        }
      });
    }

    return match;
  }

  domain(url) {
    return url.replace('http://', '').replace('https://', '').split('/')[0];
  }

  static MATCH = 'LINKIFY_MATCH';
  parseCounter = 0;

  parseString(string) {
    const elements = [];
    if (string === '') {
      return elements;
    }

    const matches = this.getMatches(string);
    if (!matches) {
      return string;
    }

    let lastIndex = 0;
    matches.forEach((match, idx) => {
      // Push the preceding text if there is any
      if (match.index > lastIndex) {
        elements.push(string.substring(lastIndex, match.index));
      }
      // Shallow update values that specified the match
      const props = {
        href: match.url,
        key: `parse${this.parseCounter}match${idx}`,
      };
      for (const key in this.props.properties) {
        if ({}.hasOwnProperty.call(this.props.children, key)) {
          let val = this.props.properties[key];
          if (val === Linkify.MATCH) {
            val = match.url;
          }

          props[key] = val;
        }
      }

      // check if the current host does not match the url then add target blank
      const currentHost = this.domain(__SERVER__ ? '' : window.location.href);
      const matchedHost = this.domain(match.url);

      if (matchedHost !== '' && matchedHost !== currentHost) {
        props.target = '_blank';
        props.rel = 'nofollow noopener noreferrer';
      }

      elements.push(
        React.createElement(this.props.component, props, match.text)
      );
      lastIndex = match.lastIndex;
    });

    if (lastIndex < string.length) {
      elements.push(string.substring(lastIndex));
    }

    return elements.length === 1 ? elements[0] : elements;
  }

  parse(children) {
    let parsed = children;

    if (typeof children === 'string') {
      parsed = this.parseString(children);
    } else if (
      React.isValidElement(children) &&
      children.type !== 'a' &&
      children.type !== 'button'
    ) {
      parsed = React.cloneElement(
        children,
        { key: `parse${++this.parseCounter}` },
        this.parse(children.props.children)
      );
    } else if (children instanceof Array) {
      parsed = children.map((child) => {
        return this.parse(child);
      });
    }

    return parsed;
  }

  render() {
    this.parseCounter = 0;
    const parsedChildren = this.parse(this.props.children);

    return <span className={this.props.className}>{parsedChildren}</span>;
  }
}

export default Linkify;
